import { useTranslation } from "react-i18next";
import { useForm, useWatch } from "react-hook-form";
import { HiOutlineCalculator, HiOutlineCheckCircle } from "react-icons/hi2";
import { HiOutlineSave } from "react-icons/hi";
import Button from "components/button/Button";
import {
  RAW_METRIC_CODES,
  QuantitativeFormData,
  RawMetricCode,
  MetricData,
  QuantitativeValueType,
  QuantitativeValuesPayload,
  QuantitativeValueItem,
} from "types/assessmentTypes";
import { useScope } from "hooks/useScope";
import { useMemo, useCallback, useState, useEffect } from "react";
import { useCalendarConfig } from "hooks/useCalendarConfig";
import { useModal } from "hooks/useModal";
import ConfirmModal from "components/modal/ConfirmModal";
import { QuantitativeField } from "./QuantitativeField";
import { Company } from "types/companyTypes";

interface QuantitativeFormProps {
  onSubmit: (data: QuantitativeValuesPayload) => Promise<void>;
  isSubmitting: boolean;
  assessmentId: number;
  initialValues?: QuantitativeValueType[];
  isCompleted?: boolean;
  company?: Company;
}

export interface QuantitativeFormDataExtended extends QuantitativeFormData {
  CURRENT_EMPLOYEES?: { value?: number };
  ACTIVE_PATENTS?: { value?: number };
  NEW_PATENT_REQUESTS?: { value?: number };
}

export const METRICS_WITH_UNIT: RawMetricCode[] = [
  "NOMINAL_CAPACITY",
  "ACTUAL_PRODUCTION",
  "EXPORT_VOLUME",
];

export const METRICS_FOR_COMPANY: RawMetricCode[] = [
  "NET_SALES_KNOWLEDGE_AND_TECHNOLOGIC",
];

export const METRICS_FOR_EXPORT: RawMetricCode[] = [
  "EXPORT_AMOUNT",
  "EXPORT_VOLUME",
];

export const SINGLE_VALUE_METRICS: RawMetricCode[] = [
  "CURRENT_EMPLOYEES",
  "ACTIVE_PATENTS",
  "NEW_PATENT_REQUESTS",
];

export default function QuantitativeForm({
  onSubmit,
  isSubmitting,
  assessmentId,
  initialValues,
  isCompleted = false,
  company,
}: QuantitativeFormProps) {
  const hasExport = Boolean(company?.hasExport);

  // hooks states
  const { t } = useTranslation();
  const scope = useScope();
  const isScopeUser = scope === "user";
  const isReadOnly = isCompleted || !isScopeUser;
  const confirmModal = useModal();
  const [pendingPayload, setPendingPayload] =
    useState<QuantitativeValuesPayload | null>(null);

  const { getYears, getYearLabel, getGregorianYear, currentLanguage } =
    useCalendarConfig();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const YEARS = useMemo(() => getYears(), [currentLanguage]);

  const getGregorianYearMemo = useCallback(
    (year: number) => getGregorianYear(year),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentLanguage]
  );

  const YEARLY_METRICS = useMemo(
    () =>
      RAW_METRIC_CODES.filter((code) => {
        if (SINGLE_VALUE_METRICS.includes(code)) {
          return false;
        }

        const isKnowledgeOrTech =
          company?.isKnowledgeBased || company?.isTechnologist;

        if (!isKnowledgeOrTech && METRICS_FOR_COMPANY.includes(code)) {
          return false;
        }

        if (!hasExport && METRICS_FOR_EXPORT.includes(code)) {
          return false;
        }

        return true;
      }),
    [company?.isKnowledgeBased, company?.isTechnologist, hasExport]
  );

  const buildDefaultValues = useCallback((): QuantitativeFormDataExtended => {
    const defaults: QuantitativeFormDataExtended = {};

    // Initialize yearly metrics with blank values
    YEARLY_METRICS.forEach((code) => {
      const metricData: MetricData = {};
      YEARS.forEach((year) => {
        metricData[year] = undefined;
      });
      if (METRICS_WITH_UNIT.includes(code as RawMetricCode)) {
        metricData.unit = code === "EXPORT_VOLUME" ? undefined : "TON";
      }
      defaults[code] = metricData;
    });

    // Initialize single value metrics with blank values
    SINGLE_VALUE_METRICS.forEach((code) => {
      defaults[code] = { value: undefined };
    });

    // Override with initial values if available
    if (initialValues?.length) {
      // Process yearly metrics
      YEARLY_METRICS.forEach((code) => {
        const metricItems = initialValues.filter(
          (item) => item.rawMetricCode === code
        );

        if (metricItems.length) {
          metricItems.forEach((item) => {
            // Find matching solar year
            const solarYear = YEARS.find(
              (y) => getGregorianYearMemo(y) === item.year
            );
            if (solarYear) {
              const numericValue = parseFloat(item.value);
              if (!isNaN(numericValue)) {
                (defaults[code] as MetricData)[solarYear] = numericValue;
              }
            }
          });

          if (METRICS_WITH_UNIT.includes(code as RawMetricCode)) {
            const unitItem = initialValues.find(
              (item) =>
                item.rawMetricCode === code &&
                item.year === getGregorianYearMemo(YEARS[0])
            );
            if (unitItem?.unit) {
              (defaults[code] as MetricData).unit = unitItem.unit;
            }
          }
        }
      });

      // Process single value metrics
      SINGLE_VALUE_METRICS.forEach((code) => {
        const items = initialValues.filter(
          (item) => item.rawMetricCode === code
        );
        if (items.length) {
          const numericValue = parseFloat(items[0].value);
          if (!isNaN(numericValue)) {
            defaults[code] = { value: numericValue };
          }
        }
      });

      // Sync units
      const nominalUnit = (defaults["NOMINAL_CAPACITY"] as MetricData)?.unit;
      const actualUnit = (defaults["ACTUAL_PRODUCTION"] as MetricData)?.unit;

      if (nominalUnit && actualUnit && nominalUnit !== actualUnit) {
        (defaults["ACTUAL_PRODUCTION"] as MetricData).unit = nominalUnit;
      } else if (nominalUnit) {
        (defaults["ACTUAL_PRODUCTION"] as MetricData).unit = nominalUnit;
      } else if (actualUnit) {
        (defaults["NOMINAL_CAPACITY"] as MetricData).unit = actualUnit;
      }
    }

    return defaults;
  }, [YEARLY_METRICS, initialValues, YEARS, getGregorianYearMemo]);

  const { control, handleSubmit, setValue, reset } =
    useForm<QuantitativeFormDataExtended>({
      defaultValues: buildDefaultValues(),
    });

  const watchedValues = useWatch({ control });

  useEffect(() => {
    reset(buildDefaultValues());
  }, [buildDefaultValues, reset]);

  const watchedNominalUnit = useWatch({
    control,
    name: "NOMINAL_CAPACITY.unit",
  });

  // Sync unit between NOMINAL_CAPACITY and ACTUAL_PRODUCTION
  useMemo(() => {
    if (watchedNominalUnit) {
      setValue("ACTUAL_PRODUCTION.unit", watchedNominalUnit);
    }
  }, [watchedNominalUnit, setValue]);

  const preparePayload = useCallback(
    (data: QuantitativeFormDataExtended): QuantitativeValueItem[] => {
      const values: QuantitativeValueItem[] = [];

      const isMetricData = (item: any): item is MetricData => {
        return item && typeof item === "object" && !("value" in item);
      };

      const isSingleValue = (item: any): item is { value?: number } => {
        return item && typeof item === "object" && "value" in item;
      };

      // Process yearly metrics - send Gregorian years
      YEARLY_METRICS.forEach((code) => {
        const metricData = data[code];
        if (metricData && isMetricData(metricData)) {
          YEARS.forEach((solarYear) => {
            const value = metricData[solarYear];
            if (value !== undefined && value !== null && !isNaN(value)) {
              const gregorianYear = getGregorianYear(solarYear);
              const item: QuantitativeValueItem = {
                rawMetricCode: code,
                year: gregorianYear,
                value: Number(value),
              };

              if (METRICS_WITH_UNIT.includes(code as RawMetricCode)) {
                if (code === "EXPORT_VOLUME") {
                  if (metricData.unit) {
                    item.unit = metricData.unit;
                  }
                } else {
                  item.unit = metricData.unit || "TON";
                }
              }

              values.push(item);
            }
          });
        }
      });

      // Process single value metrics
      SINGLE_VALUE_METRICS.forEach((code) => {
        const metricData = data[code];
        if (metricData && isSingleValue(metricData)) {
          const value = metricData.value;
          if (value !== undefined && value !== null && !isNaN(value)) {
            values.push({
              rawMetricCode: code,
              year: getGregorianYear(YEARS[0]),
              value: Number(value),
            });
          }
        }
      });

      return values;
    },
    [YEARLY_METRICS, YEARS, getGregorianYear]
  );

  const onSubmitHandler = useCallback(
    async (data: QuantitativeFormDataExtended) => {
      const values = preparePayload(data);
      const payload: QuantitativeValuesPayload = {
        assessmentId: assessmentId,
        values: values,
      };

      setPendingPayload(payload);
      confirmModal.openModal();
    },
    [preparePayload, assessmentId, confirmModal]
  );

  const handleConfirmSubmit = useCallback(async () => {
    if (pendingPayload) {
      await onSubmit(pendingPayload);
      setPendingPayload(null);
    }
    confirmModal.closeModal();
  }, [onSubmit, pendingPayload, confirmModal]);

  const handleCancelSubmit = useCallback(() => {
    setPendingPayload(null);
    confirmModal.closeModal();
  }, [confirmModal]);

  const isValidNumber = useCallback((value: any): boolean => {
    return value !== undefined && value !== null && !isNaN(value);
  }, []);

  // Form validation - all fields must be filled (including 0)
  const isFormComplete = useMemo(() => {
    const data = watchedValues as QuantitativeFormDataExtended;
    const latestYear = YEARS[0];

    for (const code of YEARLY_METRICS) {
      const metricData = data[code] as MetricData;
      const value = metricData?.[latestYear];
      if (!isValidNumber(value)) {
        return false;
      }

      if (
        code === "EXPORT_VOLUME" &&
        METRICS_WITH_UNIT.includes(code as RawMetricCode)
      ) {
        const unitValue = metricData?.unit;
        if (!unitValue || unitValue === "" || unitValue === "OTHER") {
          return false;
        }
      }
    }

    for (const code of SINGLE_VALUE_METRICS) {
      const metricData = data[code] as { value?: number };
      const value = metricData?.value;
      if (!isValidNumber(value)) {
        return false;
      }
    }

    return true;
  }, [watchedValues, YEARLY_METRICS, YEARS, isValidNumber]);

  return (
    <>
      <div className="rounded-2xl bg-white shadow-lg dark:bg-navy-800">
        {/* Header */}
        <div className="border-b border-gray-200 p-4 dark:border-navy-700">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="rounded-lg bg-brand-100 p-2 dark:bg-brand-900/30">
                <HiOutlineCalculator className="h-5 w-5 text-brand-600 dark:text-brand-400" />
              </div>
              <div>
                <h3 className="text-base font-semibold text-navy-800 dark:text-white">
                  {t("assessment.detail.quantitative.title")}
                </h3>
                <p className="text-xs text-gray-700 dark:text-gray-400">
                  {t("assessment.detail.quantitative.description")}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit(onSubmitHandler)} className="p-4">
          <div className="overflow-x-auto">
            <div className="min-w-[750px]">
              {/* Table Header */}
              <div className="mb-2 grid grid-cols-6 gap-2 rounded-lg bg-gray-50 p-2 dark:bg-navy-700">
                <div className="col-span-2 px-2 text-start">
                  <span className="text-xs font-semibold text-gray-800 dark:text-gray-300">
                    {t("assessment.detail.quantitative.metric")}
                  </span>
                </div>
                <div className="col-span-1 px-2 text-center">
                  <span className="text-xs font-semibold text-gray-800 dark:text-gray-300">
                    {t("assessment.detail.quantitative.unit")}
                  </span>
                </div>
                {YEARS.map((year, index) => (
                  <div key={year} className="col-span-1 px-2 text-center">
                    <span className="text-xs font-semibold text-gray-800 dark:text-gray-300">
                      {index === 0 && (
                        <span
                          className="me-1 text-xs font-semibold text-red-500"
                          title={t(
                            "assessment.detail.quantitative.currentYearRequired"
                          )}
                        >
                          *
                        </span>
                      )}
                      {getYearLabel(year)}
                    </span>
                    <span className="ms-1 text-[10px] text-gray-700 dark:text-gray-500">
                      ({t(`assessment.detail.quantitative.years.${index}`)})
                    </span>
                  </div>
                ))}
              </div>

              {/* Form Fields */}
              <div className="space-y-1">
                {/* Yearly Metrics */}
                {YEARLY_METRICS.map((code, index) => (
                  <QuantitativeField
                    key={code}
                    code={code}
                    index={index}
                    control={control}
                    isReadOnly={isReadOnly}
                    watchedNominalUnit={watchedNominalUnit}
                    isFirstUnit={code === "NOMINAL_CAPACITY"}
                    years={YEARS}
                  />
                ))}

                {/* Single Value Metrics */}
                {SINGLE_VALUE_METRICS.map((code, index) => (
                  <QuantitativeField
                    key={`${code}-single`}
                    code={code}
                    index={YEARLY_METRICS.length + index}
                    control={control}
                    isReadOnly={isReadOnly}
                    isSingleValue
                    years={YEARS}
                  />
                ))}
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-gray-200 pt-4 dark:border-navy-700">
            <div className="flex flex-wrap items-center gap-3">
              {isCompleted ? (
                <span className="flex items-center gap-1.5 text-sm text-green-600 dark:text-green-400">
                  <HiOutlineCheckCircle className="h-4 w-4" />
                  {t("assessment.detail.quantitative.completed")}
                </span>
              ) : !isScopeUser ? (
                <span className="flex items-center gap-1.5 text-sm text-blue-600 dark:text-blue-400">
                  <HiOutlineCheckCircle className="h-4 w-4" />
                  {t("assessment.detail.quantitative.viewOnly")}
                </span>
              ) : isFormComplete ? (
                <span className="flex items-center gap-1.5 text-sm text-green-600 dark:text-green-400">
                  <HiOutlineCheckCircle className="h-4 w-4" />
                  {t("assessment.detail.quantitative.readyToSubmit")}
                </span>
              ) : (
                <span className="flex items-center gap-1.5 text-sm text-amber-600 dark:text-amber-400">
                  <HiOutlineCheckCircle className="h-4 w-4" />
                  {t("assessment.detail.quantitative.needCompleteYear")}
                </span>
              )}
            </div>

            {!isReadOnly && (
              <Button
                type="submit"
                variant="primary"
                loading={isSubmitting}
                disabled={!isFormComplete}
                className="px-4 py-2 text-sm"
                startIcon={<HiOutlineSave className="h-4 w-4" />}
              >
                {t("assessment.detail.quantitative.submit")}
              </Button>
            )}
          </div>
        </form>
      </div>

      {/* Confirmation Modal */}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        onClose={handleCancelSubmit}
        handler={handleConfirmSubmit}
        loading={isSubmitting}
        title={t("assessment.detail.quantitative.confirmTitle")}
        desc={t("assessment.detail.quantitative.confirmMessage")}
        primary
      />
    </>
  );
}
